import { prepareAndSendItemViewEvents } from 'client/widget-components/ecom/stores/productStateAnalyticsService';
import { eventsService } from 'client/widget-components/services/events-service/eventsService';
import { autorun, computed, configure, makeAutoObservable, toJS } from 'mobx';
import CustomizationsState from './CustomizationsState';
import {
    buildProductData,
    DEFAULT_VARIATION_ID,
    findInitialVariation,
} from './productData';
import {
    OptionsVariationsType,
    ProductEntity,
    ProductPaymentPlan,
    ProductType,
    VariationType,
} from './types';
import {
    createProductPaymentPlanOptionList,
    decodeUniqueKey,
    encodeUniqueKey,
} from './utils';

configure({ isolateGlobalState: true });
export { DEFAULT_VARIATION_ID };

export class ProductState {
    product: ProductType;
    dropdownOptions: OptionsVariationsType = {};
    paymentPlanOptions: ProductPaymentPlan[] = [];
    variations: Map<string, VariationType> = new Map();
    selectedVariationKey: string = '';
    selectedPaymentPlan: ProductPaymentPlan | null = null;
    customizations: CustomizationsState;

    constructor(product: ProductType) {
        makeAutoObservable(this, {
            selectedVariation: computed,
            productData: computed,
        });
        this.product = product;
        this.dropdownOptions = product.options;
        this.paymentPlanOptions = createProductPaymentPlanOptionList(product);
        this.selectedPaymentPlan = this.paymentPlanOptions[0];

        this.customizations = new CustomizationsState(
            product.customizations || []
        );
        this.initVariations(product.variations);
        this.prepareAndSendGMEvent();
    }

    private prepareAndSendGMEvent() {
        if (!this.productData) {
            return;
        }
        prepareAndSendItemViewEvents(this.productData);
    }

    initVariations(variations: VariationType[] = []) {
        const isVariationsPriceDifferentFromProductPrice = !!variations.find(
            (variation) => variation.price !== this.product.price
        );
        const shouldModifyLabels =
            Object.keys(this.dropdownOptions)?.length === 1 &&
            isVariationsPriceDifferentFromProductPrice;
        variations.forEach((variation) => {
            const optionValues = variation.selected_options_values;
            if (optionValues) {
                this.variations.set(encodeUniqueKey(optionValues), variation);
                if (shouldModifyLabels) {
                    const optionToVal = Object.entries(
                        JSON.parse(optionValues)
                    );
                    const [optionKey, optionValue] = optionToVal[0];
                    const current = this.dropdownOptions[optionKey].find(
                        (item) => item.value === optionValue
                    );
                    if (current) {
                        current.label = `${current.label} - ${variation.displayed_price}`;
                    }
                }
            }
        });
        const initialOptionValues =
            findInitialVariation(variations)?.selected_options_values;
        if (!this.selectedVariationKey && initialOptionValues) {
            this.selectedVariationKey = encodeUniqueKey(
                JSON.parse(initialOptionValues)
            );
        }
    }

    getSelectedField(key: string) {
        const fieldValue = decodeUniqueKey(this.selectedVariationKey)[key];
        if (fieldValue && this.dropdownOptions[key]) {
            return this.dropdownOptions[key].find(
                (op) => op.value === fieldValue
            );
        }
    }

    get selectedVariation() {
        return this.variations.get(this.selectedVariationKey);
    }

    get productData(): ProductEntity {
        return buildProductData({
            product: this.product,
            variation: toJS(this.selectedVariation),
            paymentPlan: this.selectedPaymentPlan,
            surcharge: this.customizations.pricesSurcharge,
        });
    }

    selectPaymentPlanOption = (option: ProductPaymentPlan) => {
        this.selectedPaymentPlan = option;
    };

    updateVariation(variationName: string, variationValue: string) {
        if (this.selectedVariation?.selected_options_values) {
            const optionValues = JSON.parse(
                this.selectedVariation.selected_options_values
            );
            this.selectedVariationKey = encodeUniqueKey({
                ...optionValues,
                [variationName]: variationValue,
            });
            this.updatePaymentPlanOptions();
            this.prepareAndSendGMEvent();
        }
    }

    updatePaymentPlanOptions = () => {
        if (!this.selectedVariation) {
            return;
        }

        const selectedVariation = this.selectedVariation;

        this.paymentPlanOptions = this.paymentPlanOptions.map((plan) => {
            const paymentPlanPricesForSelectedVariation =
                selectedVariation.plans_prices.find(
                    (planPrices) => planPrices.id === plan.id
                );
            return {
                ...plan,
                plan_price:
                    paymentPlanPricesForSelectedVariation?.price ||
                    selectedVariation.price,
                plan_displayed_price:
                    paymentPlanPricesForSelectedVariation?.displayed_price ||
                    selectedVariation.displayed_price,
            };
        });
    };

    onSelectedValuesChange(
        callback: (selectedVariation?: ProductEntity) => void
    ) {
        const imageToIndex = this.product.images.reduce(
            (result: { [key: string]: number }, image, index) => ({
                ...result,
                [image.image]: index,
            }),
            {}
        );

        autorun(() => {
            const variationImage = this.productData.image;
            const newIndex = imageToIndex[variationImage];
            eventsService.dispatch(
                'selected-image-changed',
                'dynamic_page_collection.images',
                { newIndex, newSrc: variationImage }
            );
            callback(this.productData);
        });
    }
}
